import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service, Timeframe } from 'webapp-types';
import { ForecastMethod } from 'webapp/enums/enums';

import ForecastsSettingsLinearChangeInputComponent, {
	LinearChangePattern,
} from 'webapp/components/forecasts/settings/linear-change/input';
import FormRadiobuttonComponent from 'webapp/components/form/radiobutton';
import FormSelectComponent from 'webapp/components/form/select';

import type { ForecastMethodology } from 'webapp/models-custom/forecast';
import type Forecast from 'webapp/models-custom/forecast';

const FORECAST_METHOD_VALUES = {
	DATABOX: 'databox_forecasting_methodology',
	LINEAR_REGRESSION: 'linear_regression',
	SIMPLE_STATISTICAL: 'simple_statistical',
};

interface Args {
	forecast: Forecast;
	isDisabled: boolean;
	onChange: (methodology: ForecastMethodology) => void;
}

export default class ForecastsSettingsForecastMethodologyComponent extends Component<Args> {
	@service('model-manager/forecasts') declare mmForecastsService: Service.ModelManager.Forecasts;
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;

	@tracked selectedStatisticalMethod: ForecastMethod = ForecastMethod.SIMPLE_EXPONENTIAL;
	@tracked selectedLinearChangePattern: LinearChangePattern = LinearChangePattern.MONTHLY_PERCENT;
	@tracked linearChangeValue: number | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (this.args.forecast.methodology) {
			if (this.mmForecastsService.isStatisticalMethod(this.args.forecast.methodology.type)) {
				this.selectedStatisticalMethod = this.args.forecast.methodology.type;
			}
			// load linear change settings from extra
			if (this.args.forecast.methodology.extra) {
				const extra = this.args.forecast.methodology.extra;
				this.linearChangeValue = extra.changeValue || null;
				this.selectedLinearChangePattern =
					(extra.changePattern as LinearChangePattern) ||
					LinearChangePattern.MONTHLY_PERCENT;
			}
		}
	}

	get selectedMethod(): ForecastMethod {
		return this.args.forecast.methodology?.type ?? ForecastMethod.DATABOX;
	}

	get methodValues() {
		return FORECAST_METHOD_VALUES;
	}

	get statisticalMethodOptions() {
		return [
			{
				value: ForecastMethod.SIMPLE_EXPONENTIAL,
				label: 'Simple exponential smoothing',
			},
			{
				value: ForecastMethod.LINEAR_CHANGE,
				label: 'Linear change',
			},
			{
				value: ForecastMethod.MOVING_AVERAGE_6M,
				label: '6-months moving average',
			},
			//TODO @marcel uncomment when supported on BE
			// {
			// 	value: ForecastMethod.AVERAGE,
			// 	label: 'Average',
			// },
		];
	}

	get isDataboxMethodSelected() {
		return this.selectedMethod === ForecastMethod.DATABOX;
	}

	get isLinearRegressionSelected() {
		return this.selectedMethod === ForecastMethod.LINEAR_REGRESSION;
	}

	get isSimpleStatisticalSelected() {
		return this.mmForecastsService.isStatisticalMethod(this.selectedMethod);
	}

	get isLinearChangeSelected() {
		return this.selectedMethod === ForecastMethod.LINEAR_CHANGE;
	}

	get simpleStatisticalRadioValue() {
		return this.isSimpleStatisticalSelected
			? FORECAST_METHOD_VALUES.SIMPLE_STATISTICAL
			: this.selectedMethod;
	}

	get hasRegressors(): boolean {
		return this.args.forecast.regressors.length > 0;
	}

	get isLinearRegressionDisabled(): boolean {
		return !this.hasRegressors;
	}

	get isForecastModelingEnabled(): boolean {
		return this.billingService.forecastModelingEnabled;
	}

	get isLinearRegressionOptionDisabled(): boolean {
		return this.args.isDisabled || this.isLinearRegressionDisabled;
	}

	get linearRegressionTooltipText(): string | undefined {
		if (this.isLinearRegressionDisabled) {
			return 'At least one impacting metric is required for linear regression methodology.';
		}
		return undefined;
	}

	get databoxMethodLabel(): string {
		return this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins
			? 'Advanced forecasting methodology'
			: 'Databox forecasting methodology';
	}

	get databoxMethodDescription(): string {
		const brandName = this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins
			? 'advanced'
			: "Databox's";
		return `Forecast future values using ${brandName} method, which combines time series and linear regression models. Forecast values will update automatically when impacting metrics are added or their future values are changed.`;
	}

	get statisticalMethodDescription() {
		if (!this.isSimpleStatisticalSelected) {
			return '';
		}

		switch (this.selectedMethod) {
			case ForecastMethod.SIMPLE_EXPONENTIAL:
				return "Simple exponential smoothing method gives more weight to recent data points, making the forecast more responsive to recent changes. The future values won't update automatically if impacting metrics are added or changed.";
			case ForecastMethod.LINEAR_CHANGE:
				return "Simple linear increase/decrease is calculated based on chosen target setup. The future values won't update automatically if impacting metrics are added or changed.";
			case ForecastMethod.MOVING_AVERAGE_6M:
				return "The 6-month moving average method uses the average of previous 6 months to calculate future values. The future values won't update automatically if impacting metrics are added or changed.";
			//TODO @marcel uncomment when supported on BE
			// case ForecastMethod.AVERAGE:
			// 	return "Simple average method calculates the average of all available past values. Future values won't update automatically when impacting metrics are added or changed.";
			default:
				return '';
		}
	}

	get forecastTimeframe(): Timeframe | null {
		return this.args.forecast.timeframe;
	}

	get forecastEndDate(): string | null {
		return this.args.forecast.endDate;
	}

	@action
	changeMethod(value: AnySimpleValue) {
		const method = value as ForecastMethod;
		let newMethod: ForecastMethod;

		if (
			method === FORECAST_METHOD_VALUES.SIMPLE_STATISTICAL &&
			this.selectedStatisticalMethod
		) {
			newMethod = this.selectedStatisticalMethod;
		} else {
			newMethod = method;
		}

		// update the forecast methodology directly
		this.args.forecast.methodology = {
			type: newMethod,
			extra:
				newMethod === ForecastMethod.LINEAR_CHANGE
					? {
							changeValue: this.linearChangeValue ?? undefined,
							changePattern: this.selectedLinearChangePattern,
						}
					: null,
		};

		this.args.onChange(this.args.forecast.methodology);
	}

	@action
	changeStatisticalMethod(value: string | null) {
		const method = value as ForecastMethod;
		this.selectedStatisticalMethod = method;

		// update the forecast methodology directly
		this.args.forecast.methodology = {
			type: method,
			extra:
				method === ForecastMethod.LINEAR_CHANGE
					? {
							changeValue: this.linearChangeValue ?? undefined,
							changePattern: this.selectedLinearChangePattern,
						}
					: null,
		};

		this.args.onChange(this.args.forecast.methodology);
	}

	@action
	changeLinearChangeValue(value: number | null) {
		this.linearChangeValue = value;

		// update the forecast methodology directly if it's linear change
		if (this.selectedMethod === ForecastMethod.LINEAR_CHANGE) {
			this.args.forecast.methodology = {
				type: ForecastMethod.LINEAR_CHANGE,
				extra: {
					changeValue: value ?? undefined,
					changePattern: this.selectedLinearChangePattern,
				},
			};
			// only trigger onChange if value is provided (required for LINEAR_CHANGE)
			if (value !== null && value !== undefined) {
				this.args.onChange(this.args.forecast.methodology);
			}
		}
	}

	@action
	changeLinearChangePattern(pattern: LinearChangePattern) {
		this.selectedLinearChangePattern = pattern;

		// Update the forecast methodology directly if it's linear change
		if (this.selectedMethod === ForecastMethod.LINEAR_CHANGE) {
			this.args.forecast.methodology = {
				type: ForecastMethod.LINEAR_CHANGE,
				extra: {
					changeValue: this.linearChangeValue ?? undefined,
					changePattern: pattern,
				},
			};
			// only trigger onChange if value is provided (required for LINEAR_CHANGE)
			if (this.linearChangeValue !== null && this.linearChangeValue !== undefined) {
				this.args.onChange(this.args.forecast.methodology);
			}
		}
	}
	<template>
		<div class="forecast-methodology-section">
			<div class="radio radiobutton-default methodology-option">
				<FormRadiobuttonComponent
					@name="forecastMethod"
					@value={{this.methodValues.DATABOX}}
					@selected={{this.selectedMethod}}
					@label={{this.databoxMethodLabel}}
					@isDisabled={{@isDisabled}}
					@onChange={{this.changeMethod}}
				/>
				{{#if this.isDataboxMethodSelected}}
					<div class="methodology-description">
						<p>
							{{this.databoxMethodDescription}}
							{{#unless
								this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins
							}}
								<a
									href="https://help.databox.com/databox-forecasting-methodology"
									target="_blank"
								>
									Learn more
								</a>
							{{/unless}}
						</p>
					</div>
				{{/if}}
			</div>

			{{#if this.isForecastModelingEnabled}}
				<div class="radio radiobutton-default methodology-option">
					<FormRadiobuttonComponent
						@name="forecastMethod"
						@value={{this.methodValues.LINEAR_REGRESSION}}
						@selected={{this.selectedMethod}}
						@label="Linear regression"
						@isDisabled={{this.isLinearRegressionOptionDisabled}}
						@tooltipText={{this.linearRegressionTooltipText}}
						@onChange={{this.changeMethod}}
					/>
					{{#if this.isLinearRegressionSelected}}
						<div class="methodology-description">
							<p>
								A linear regression model identifies trends and patterns in the main
								metric, as well as the influence of impacting metrics in historical
								data, to predict a linear forecast. Forecast values will update
								automatically when impacting metrics are added or their future
								values are changed.
								{{#unless
									this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins
								}}
									<a
										href="https://help.databox.com/databox-forecasting-methodology"
										target="_blank"
									>
										Learn more
									</a>
								{{/unless}}
							</p>
						</div>
					{{/if}}
				</div>

				<div class="radio radiobutton-default methodology-option">
					<FormRadiobuttonComponent
						@name="forecastMethod"
						@value={{this.methodValues.SIMPLE_STATISTICAL}}
						@selected={{this.simpleStatisticalRadioValue}}
						@label="Simple statistical methods"
						@isDisabled={{@isDisabled}}
						@onChange={{this.changeMethod}}
					/>
					{{#if this.isSimpleStatisticalSelected}}
						<div class="methodology-description">
							<div class="statistical-method-select">
								<FormSelectComponent
									name="statisticalMethod"
									@selected={{this.selectedStatisticalMethod}}
									@options={{this.statisticalMethodOptions}}
									@onChange={{this.changeStatisticalMethod}}
									@isRequired={{true}}
									@isNumeric={{false}}
									@showFloatlabel={{false}}
									@isDisabled={{@isDisabled}}
								/>
							</div>
							{{#if this.isLinearChangeSelected}}
								<div class="linear-change-controls">
									<ForecastsSettingsLinearChangeInputComponent
										@value={{this.linearChangeValue}}
										@changePattern={{this.selectedLinearChangePattern}}
										@onChangeValue={{this.changeLinearChangeValue}}
										@onChangePattern={{this.changeLinearChangePattern}}
										@isDisabled={{@isDisabled}}
										@timeframe={{this.forecastTimeframe}}
										@endDate={{this.forecastEndDate}}
									/>
								</div>
							{{/if}}
							<p>
								{{this.statisticalMethodDescription}}

								{{#unless
									this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins
								}}
									<a
										href="https://help.databox.com/databox-forecasting-methodology"
										target="_blank"
									>
										Learn more
									</a>
								{{/unless}}
							</p>
						</div>
					{{/if}}
				</div>
			{{/if}}
		</div>
	</template>
}
